import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import {
  ConsultationVitalsSection,
  emptyVitalsFormState,
  formStateToVitalsInput,
} from "@/components/clinical/consultation-vitals-form";
import { PatientFormModal } from "@/components/patients/PatientFormModal";
import { PageHeader } from "@/components/PageHeader";
import { ServiceBillingBadge } from "@/components/billing/ServiceBillingBadge";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import {
  createAdmission,
  listAdmissions,
  orientAdmission,
  syncAdmissionsFromApi,
  updateAdmissionStatus,
  updateAdmissionTriage,
  updateEmergencyTriage,
} from "@/core/admissions/admission-store";
import {
  ADMISSION_STATUS_LABELS,
  DESTINATION_OPTIONS,
  EMERGENCY_ARRIVAL_LABELS,
  EMERGENCY_EXTRA_DESTINATIONS,
  TRIAGE_LABELS,
  TRIAGE_SHORT,
  type Admission,
  type EmergencyArrivalMode,
  type TriageLevel,
} from "@/core/admissions/types";
import { listActiveMedicalUnitNames } from "@/core/admin/establishment-store";
import { billingSnapshotForAdmission } from "@/core/billing/service-billing-status";
import {
  createVitalSignRecord,
  listVitalSignRecordsByAdmission,
  VitalSignValidationError,
} from "@/core/nursing/vital-sign-store";
import { formatVitalSignRecordSummary } from "@/core/nursing/types";
import {
  isEmergencyP3NotificationsEnabled,
  setEmergencyP3NotificationsEnabled,
} from "@/core/notifications/emergency-notification-settings";
import { notifyEmergencyAdmission } from "@/core/notifications/emergency-notify";
import {
  createPatient,
  getPatientById,
  listPatients,
  patientDetailPath,
} from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import { canWriteAdmissions } from "@/core/permissions";
import { routes } from "@/router/all_routes";

const TRIAGE_ORDER: TriageLevel[] = ["red", "yellow", "green"];

function TriageBadge({ level }: { level: TriageLevel }) {
  return (
    <span className={`hosp-triage-badge hosp-triage-badge--${level}`}>
      {TRIAGE_SHORT[level]} — {TRIAGE_LABELS[level]}
    </span>
  );
}

function EmergencyRow({
  admission,
  onRefresh,
  canWrite,
}: {
  admission: Admission;
  onRefresh: () => void;
  canWrite: boolean;
}) {
  const auth = useAuth();
  const patient = getPatientById(admission.patientId);
  const destinations = useMemo(() => {
    const units = listActiveMedicalUnitNames();
    const base = units.length > 0 ? units : [...DESTINATION_OPTIONS];
    return [...new Set([...base, ...EMERGENCY_EXTRA_DESTINATIONS])];
  }, []);
  const [destination, setDestination] = useState(
    admission.destination || destinations[0] || "Médecine générale",
  );
  const [arrivalMode, setArrivalMode] = useState<EmergencyArrivalMode>(
    admission.arrivalMode ?? "walk_in",
  );
  const [glasgowScore, setGlasgowScore] = useState(
    admission.glasgowScore != null ? String(admission.glasgowScore) : "",
  );
  const [allergiesNote, setAllergiesNote] = useState(admission.allergiesNote);
  const [triageNotes, setTriageNotes] = useState(admission.triageNotes);
  const [vitalsForm, setVitalsForm] = useState(emptyVitalsFormState);
  const [vitalsError, setVitalsError] = useState<string | null>(null);
  const [triageMsg, setTriageMsg] = useState<string | null>(null);

  const vitalsForPass = listVitalSignRecordsByAdmission(admission.id);
  const emergencyBilling = billingSnapshotForAdmission(admission.id, "emergency");
  const ambulanceBilling =
    admission.arrivalMode === "ambulance"
      ? billingSnapshotForAdmission(admission.id, "ambulance")
      : null;

  if (!patient || !admission.triageLevel) return null;

  const handleTriage = (level: TriageLevel) => {
    const previous = admission.triageLevel;
    updateAdmissionTriage(admission.id, level);
    if (!patient) {
      onRefresh();
      return;
    }
    if (level === "red" && previous !== "red") {
      void notifyEmergencyAdmission({
        admissionId: admission.id,
        patientName: patientFullName(patient),
        triageLevel: level,
        motif: admission.motif,
      });
    } else if (level === "green" && previous !== "green" && isEmergencyP3NotificationsEnabled()) {
      void notifyEmergencyAdmission({
        admissionId: admission.id,
        patientName: patientFullName(patient),
        triageLevel: level,
        motif: admission.motif,
      });
    }
    onRefresh();
  };

  const handleTakeCharge = () => {
    updateAdmissionStatus(admission.id, "in_progress");
    onRefresh();
  };

  const handleSaveTriage = (e: FormEvent) => {
    e.preventDefault();
    const glasgow =
      glasgowScore.trim() === "" ? null : Math.min(15, Math.max(3, Number(glasgowScore)));
    updateEmergencyTriage(admission.id, {
      arrivalMode,
      glasgowScore: glasgow,
      allergiesNote,
      triageNotes,
    });
    setTriageMsg("Fiche triage enregistrée.");
    onRefresh();
  };

  const handleSaveVitals = (e: FormEvent) => {
    e.preventDefault();
    setVitalsError(null);
    try {
      const staffName =
        auth.user?.displayName ??
        [auth.user?.firstName, auth.user?.lastName].filter(Boolean).join(" ") ??
        auth.user?.email ??
        "Infirmier";
      createVitalSignRecord({
        patientId: admission.patientId,
        recordedById: auth.user?.id ?? "staff-local",
        recordedByName: staffName,
        context: "emergency",
        admissionId: admission.id,
        vitals: formStateToVitalsInput(vitalsForm, null),
        notes: triageNotes || admission.motif,
      });
      setVitalsForm(emptyVitalsFormState());
      onRefresh();
    } catch (err) {
      setVitalsError(
        err instanceof VitalSignValidationError ? err.message : "Enregistrement impossible.",
      );
    }
  };

  const handleOrient = (e: FormEvent) => {
    e.preventDefault();
    orientAdmission(admission.id, [destination]);
    onRefresh();
  };

  const showTriagePanel = admission.status === "in_progress" || admission.status === "waiting";

  return (
    <>
      <tr className="ph-emergency-row">
        <td data-label="Triage">
          <TriageBadge level={admission.triageLevel} />
          <div className="btn-group btn-group-sm mt-1 d-flex flex-wrap gap-1">
            {canWrite &&
              TRIAGE_ORDER.map((level) => (
                <button
                  key={level}
                  type="button"
                  className={`btn btn-outline-secondary btn-sm ${admission.triageLevel === level ? "active" : ""}`}
                  onClick={() => handleTriage(level)}
                  title={TRIAGE_LABELS[level]}
                >
                  {TRIAGE_SHORT[level]}
                </button>
              ))}
          </div>
          {admission.glasgowScore != null && (
            <div className="small text-muted mt-1">Glasgow {admission.glasgowScore}/15</div>
          )}
        </td>
        <td data-label="Patient">
          <span className="ph-inv-badge">{patient.fileNumber}</span>
          <div className="fw-medium mt-1">
            <Link to={patientDetailPath(patient.id)}>{patientFullName(patient)}</Link>
          </div>
          <div className="small text-muted">{patient.phone || "—"}</div>
          {admission.arrivalMode && (
            <div className="small text-muted">
              {EMERGENCY_ARRIVAL_LABELS[admission.arrivalMode]}
            </div>
          )}
        </td>
        <td data-label="Motif" className="small">
          <div>{admission.motif || "—"}</div>
          {admission.allergiesNote && (
            <div className="text-danger mt-1">⚠ {admission.allergiesNote}</div>
          )}
        </td>
        <td data-label="Statut">
          <span className={`hosp-status-badge hosp-status-badge--${admission.status}`}>
            {ADMISSION_STATUS_LABELS[admission.status]}
          </span>
          <div className="small text-muted mt-1">
            {new Date(admission.createdAt).toLocaleTimeString("fr-FR", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
          {vitalsForPass[0] && (
            <div className="small text-muted mt-1" title={formatVitalSignRecordSummary(vitalsForPass[0])}>
              Constantes : {formatVitalSignRecordSummary(vitalsForPass[0]).slice(0, 40)}
              {formatVitalSignRecordSummary(vitalsForPass[0]).length > 40 ? "…" : ""}
            </div>
          )}
        </td>
        <td data-label="Facturation">
          <div className="d-flex flex-column align-items-start gap-1">
            <ServiceBillingBadge snapshot={emergencyBilling} compact />
            {ambulanceBilling && (
              <ServiceBillingBadge snapshot={ambulanceBilling} compact />
            )}
          </div>
        </td>
        <td data-label="Actions" className="text-end ph-emergency-actions">
          {admission.status === "waiting" && canWrite && (
            <button type="button" className="btn btn-primary btn-sm" onClick={handleTakeCharge}>
              Prendre en charge
            </button>
          )}
          {admission.status === "in_progress" && canWrite && (
            <form className="ph-emergency-orient-form d-inline-flex flex-column align-items-end gap-1" onSubmit={handleOrient}>
              <select
                className="form-select form-select-sm"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
              >
                {destinations.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn btn-success btn-sm">
                Orienter
              </button>
            </form>
          )}
          {admission.status === "oriented" && (
            <span className="small fw-semibold">{admission.destination}</span>
          )}
        </td>
      </tr>
      {showTriagePanel && canWrite && (
        <tr className="ph-emergency-row-expand">
          <td colSpan={6} className="ph-emergency-expand-cell bg-light">
            <div className="row g-3 py-2">
              <div className="col-lg-5">
                <h4 className="h6 fw-semibold mb-2">Fiche triage</h4>
                <form className="row g-2" onSubmit={handleSaveTriage}>
                  <div className="col-12 col-md-6">
                    <label className="form-label small mb-1">Mode d&apos;arrivée</label>
                    <select
                      className="form-select form-select-sm"
                      value={arrivalMode}
                      onChange={(e) => setArrivalMode(e.target.value as EmergencyArrivalMode)}
                    >
                      {(Object.keys(EMERGENCY_ARRIVAL_LABELS) as EmergencyArrivalMode[]).map((mode) => (
                        <option key={mode} value={mode}>
                          {EMERGENCY_ARRIVAL_LABELS[mode]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label small mb-1">Glasgow (3–15)</label>
                    <input
                      type="number"
                      min={3}
                      max={15}
                      className="form-control form-control-sm"
                      placeholder="Ex. 15"
                      value={glasgowScore}
                      onChange={(e) => setGlasgowScore(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small mb-1">Allergies / antécédents</label>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Ex. pénicilline, diabète…"
                      value={allergiesNote}
                      onChange={(e) => setAllergiesNote(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small mb-1">Notes triage</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      value={triageNotes}
                      onChange={(e) => setTriageNotes(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <button type="submit" className="btn btn-outline-primary btn-sm">
                      Enregistrer fiche triage
                    </button>
                    {triageMsg && <span className="small text-success ms-2">{triageMsg}</span>}
                  </div>
                </form>
              </div>
              <div className="col-lg-7">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="h6 fw-semibold mb-0">Constantes au triage</h4>
                  <Link to={routes.nursingCare} className="btn btn-link btn-sm p-0">
                    Module soins infirmiers
                  </Link>
                </div>
                <form onSubmit={handleSaveVitals}>
                  <ConsultationVitalsSection
                    form={vitalsForm}
                    vitals={null}
                    canEdit
                    onChange={(patch) => setVitalsForm((prev) => ({ ...prev, ...patch }))}
                  />
                  <button type="submit" className="btn btn-primary btn-sm mt-2">
                    Enregistrer constantes
                  </button>
                  {vitalsError && <p className="small text-danger mb-0 mt-2">{vitalsError}</p>}
                </form>
                {vitalsForPass.length > 0 && (
                  <ul className="list-unstyled small text-muted mt-3 mb-0">
                    {vitalsForPass.slice(0, 3).map((record) => (
                      <li key={record.id}>
                        {new Date(record.recordedAt).toLocaleTimeString("fr-FR")} —{" "}
                        {formatVitalSignRecordSummary(record)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function EmergencyPage() {
  const auth = useAuth();
  const canWrite = canWriteAdmissions(auth);
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [patientModalOpen, setPatientModalOpen] = useState(false);
  const [patientId, setPatientId] = useState("");
  const [motif, setMotif] = useState("");
  const [triageLevel, setTriageLevel] = useState<TriageLevel>("yellow");
  const [arrivalMode, setArrivalMode] = useState<EmergencyArrivalMode>("walk_in");
  const [glasgowScore, setGlasgowScore] = useState("");
  const [allergiesNote, setAllergiesNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [notifyP3, setNotifyP3] = useState(() => isEmergencyP3NotificationsEnabled());

  const admissions = useMemo(() => {
    void refreshKey;
    return listAdmissions("emergency");
  }, [refreshKey]);

  const stats = useMemo(() => {
    const counts = { red: 0, yellow: 0, green: 0, active: 0 };
    for (const a of admissions) {
      if (a.triageLevel) counts[a.triageLevel] += 1;
      if (a.status !== "oriented") counts.active += 1;
    }
    return counts;
  }, [admissions]);

  const admissionsPagination = useClientPagination(admissions, [], 25);
  const { pagedItems: pagedAdmissions } = admissionsPagination;

  const patients = useMemo(() => listPatients(), [refreshKey, patientModalOpen]);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    setSyncing(true);
    void syncAdmissionsFromApi().finally(() => {
      if (!cancelled) {
        setSyncing(false);
        setRefreshKey((k) => k + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRegister = (e: FormEvent) => {
    e.preventDefault();
    if (!patientId) {
      setFormError("Sélectionnez un patient.");
      return;
    }
    if (!motif.trim()) {
      setFormError("Indiquez le motif de passage.");
      return;
    }
    const glasgow =
      glasgowScore.trim() === "" ? null : Math.min(15, Math.max(3, Number(glasgowScore)));
    const admission = createAdmission({
      patientId,
      channel: "emergency",
      motif,
      triageLevel,
      arrivalMode,
      glasgowScore: glasgow,
      allergiesNote,
    });
    const patient = getPatientById(patientId);
    if (patient && triageLevel !== "green") {
      void notifyEmergencyAdmission({
        admissionId: admission.id,
        patientName: patientFullName(patient),
        triageLevel,
        motif,
      });
    } else if (patient && triageLevel === "green" && notifyP3) {
      void notifyEmergencyAdmission({
        admissionId: admission.id,
        patientName: patientFullName(patient),
        triageLevel,
        motif,
      });
    }
    setMotif("");
    setGlasgowScore("");
    setAllergiesNote("");
    setFormError(null);
    refresh();
  };

  return (
    <div className="ph-emergency-page">
      <PageHeader
        title="Urgences"
        description="Triage avancé, constantes et orientation des passages aux urgences."
        actions={
          <Link to={routes.admissions} className="btn btn-outline-secondary btn-sm">
            File admissions
          </Link>
        }
      />

      <div className="row g-2 g-md-3 mb-4 ph-emergency-triage-stats">
        {TRIAGE_ORDER.map((level) => (
          <div key={level} className="col-4 col-md-4">
            <div className={`hosp-triage-stat hosp-triage-stat--${level}`}>
              <span className="hosp-triage-stat-label">{TRIAGE_LABELS[level]}</span>
              <span className="hosp-triage-stat-value">{stats[level]}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="ph-invoice-sheet mb-3">
        <div className="form-check form-switch">
          <input
            id="urg-notify-p3"
            className="form-check-input"
            type="checkbox"
            checked={notifyP3}
            onChange={(e) => {
              setNotifyP3(e.target.checked);
              setEmergencyP3NotificationsEnabled(e.target.checked);
            }}
          />
          <label className="form-check-label small" htmlFor="urg-notify-p3">
            Notifier aussi les passages <strong>P3</strong> (stable) — priorité basse
          </label>
        </div>
      </div>

      <div className="ph-invoice-sheet ph-emergency-register mb-4">
        <h3 className="h6 fw-semibold mb-3">Nouveau passage aux urgences</h3>
        {canWrite ? (
        <form className="row g-3 align-items-end ph-emergency-register-form" onSubmit={handleRegister}>
          <div className="col-12 col-lg-3">
            <label className="form-label small fw-semibold mb-1" htmlFor="urg-patient">
              Patient
            </label>
            <div className="d-flex gap-2 ph-adm-patient-row">
              <select
                id="urg-patient"
                className="form-select form-select-sm"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
              >
                <option value="">— Choisir —</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {patientFullName(p)} ({p.fileNumber})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm text-nowrap"
                onClick={() => setPatientModalOpen(true)}
              >
                + Créer
              </button>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-lg-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="urg-motif">
              Motif
            </label>
            <input
              id="urg-motif"
              type="text"
              className="form-control form-control-sm"
              placeholder="Motif de passage"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-6 col-lg-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="urg-arrival">
              Arrivée
            </label>
            <select
              id="urg-arrival"
              className="form-select form-select-sm"
              value={arrivalMode}
              onChange={(e) => setArrivalMode(e.target.value as EmergencyArrivalMode)}
            >
              {(Object.keys(EMERGENCY_ARRIVAL_LABELS) as EmergencyArrivalMode[]).map((mode) => (
                <option key={mode} value={mode}>
                  {EMERGENCY_ARRIVAL_LABELS[mode]}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-sm-4 col-lg-1">
            <label className="form-label small fw-semibold mb-1" htmlFor="urg-glasgow">
              Glasgow
            </label>
            <input
              id="urg-glasgow"
              type="number"
              min={3}
              max={15}
              className="form-control form-control-sm"
              placeholder="15"
              value={glasgowScore}
              onChange={(e) => setGlasgowScore(e.target.value)}
            />
          </div>
          <div className="col-6 col-sm-4 col-lg-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="urg-triage">
              Triage
            </label>
            <select
              id="urg-triage"
              className="form-select form-select-sm"
              value={triageLevel}
              onChange={(e) => setTriageLevel(e.target.value as TriageLevel)}
            >
              {TRIAGE_ORDER.map((level) => (
                <option key={level} value={level}>
                  {TRIAGE_LABELS[level]}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-sm-4 col-lg-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="urg-allergies">
              Allergies
            </label>
            <input
              id="urg-allergies"
              className="form-control form-control-sm"
              placeholder="Optionnel"
              value={allergiesNote}
              onChange={(e) => setAllergiesNote(e.target.value)}
            />
          </div>
          <div className="col-12">
            <button type="submit" className="btn btn-danger btn-sm w-100 w-sm-auto">
              Enregistrer passage
            </button>
          </div>
        </form>
        ) : (
          <p className="small text-muted mb-0">
            Droits insuffisants pour enregistrer un passage — consultation seule.
          </p>
        )}
        {formError && <p className="small text-danger mb-0 mt-2">{formError}</p>}
        {syncing && <p className="small text-muted mb-0 mt-2">Synchronisation…</p>}
        <p className="small text-muted mb-0 mt-2">
          {stats.active} passage{stats.active !== 1 ? "s" : ""} actif{stats.active !== 1 ? "s" : ""} · tri
          prioritaire P1 → P3
        </p>
      </div>

      <div className="ph-invoice-sheet ph-emergency-list">
        {admissions.length === 0 ? (
          <p className="text-muted mb-0">Aucun passage enregistré.</p>
        ) : (
          <div className="table-responsive ph-emergency-table">
            <table className="table table-sm ph-dash-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Triage</th>
                  <th>Patient</th>
                  <th>Motif</th>
                  <th>Statut</th>
                  <th>Facturation</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedAdmissions.map((admission) => (
                  <EmergencyRow
                    key={admission.id}
                    admission={admission}
                    onRefresh={refresh}
                    canWrite={canWrite}
                  />
                ))}
              </tbody>
            </table>
            <TablePagination
              page={admissionsPagination.page}
              pageSize={admissionsPagination.pageSize}
              totalItems={admissionsPagination.totalItems}
              onPageChange={admissionsPagination.setPage}
              onPageSizeChange={admissionsPagination.setPageSize}
            />
          </div>
        )}
      </div>

      <PatientFormModal
        open={patientModalOpen}
        onClose={() => setPatientModalOpen(false)}
        onSave={(input) => {
          const created = createPatient(input);
          setPatientId(created.id);
          setPatientModalOpen(false);
          refresh();
        }}
      />
    </div>
  );
}
